import React, { FC, useEffect, useState } from 'react';

import { IArticleForm } from './articleForm.interface';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import {
 IArticleDto,
 IService,
 RoutesEnum,
 servicePaginationData,
 userRoles,
} from '../../../shared';
import { isRequired } from '../../../shared';
import {
 ArticleTypes,
 articleTypesLocales,
} from '../../../shared/constants/articleTypes';
import { serviceApi } from '../../../store/services';
import { useAppSelector } from '../../../hooks';
import styles from './articleForm.module.scss';
import { useNavigate } from 'react-router-dom';
import { CustomSelectField } from '../../FormFields';
import { Button } from '../../Buttons';

export const ArticleForm: FC<IArticleForm<IArticleDto>> = ({
 onSubmit,
 initialValues,
 setSelectedServices,
 selectedServices,
}) => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const currentUser = useAppSelector(
  ({ userReducer }) => userReducer.currentUser,
 );

 const [articlePreview, setArticlePreview] = useState<string | File[]>();

 const { data: fetchedServiceList } = serviceApi.useFetchServiceListQuery(
  servicePaginationData,
 );

 const services = fetchedServiceList?.items || [];

 const onSelectFile = (e: FileList | null) => {
  if (e) {
   const articlePreview = URL.createObjectURL(e[0]);
   setArticlePreview(articlePreview);
  }
 };

 useEffect(() => {
  if (initialValues?.imageLink) {
   setArticlePreview(initialValues?.imageLink);
  }
 }, [initialValues]);

 useEffect(() => {
  if (currentUser?.role !== userRoles.admin) {
   navigate(`${RoutesEnum.admin}${RoutesEnum.services}`);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [currentUser?.role]);

 return (
  <div className={styles.form_wrap}>
   <Form
    onSubmit={onSubmit}
    initialValues={initialValues}
    render={({
     handleSubmit,
     form,
     submitting,
     pristine,
     values,
     submitFailed,
    }) => (
     <form onSubmit={handleSubmit}>
      <Field name='title' validate={isRequired}>
       {({ input, meta }) => (
        <div
         className={`${styles.input_wrap} ${
          meta.error && meta.touched ? styles.form_input_error : ''
         }`}
        >
         <label>{t('adminPanel.form.title')}*</label>
         <input
          {...input}
          type='text'
          placeholder={t('adminPanel.form.title')}
         />
         {meta.error && meta.touched && <span>{t(meta.error)}</span>}
        </div>
       )}
      </Field>
      <div className={styles.article_images_wrap}>
       <Field
        type='file'
        name='image'
        validate={!initialValues ? isRequired : undefined}
       >
        {({ input: { value, onChange, ...input }, meta }) => (
         <div className={styles.input_preview_wrap}>
          <div
           className={`${styles.input_file_wrap} ${
            meta.error && meta.touched ? styles.form_input_error : ''
           }`}
          >
           <label>
            {t('adminPanel.form.articleImage')}
            {!initialValues && '*'}
           </label>
           <input
            {...input}
            onChange={({ target }) => {
             onChange(target.files);
             onSelectFile(target.files);
            }}
            type='file'
            id='image'
            name='image'
            accept='image/*'
           />
           {meta.error && meta.touched && <span>{t(meta.error)}</span>}
          </div>
          {articlePreview && (
           <div className={styles.preview}>
            <img src={articlePreview as string} alt='downloaded_image' />
           </div>
          )}
         </div>
        )}
       </Field>
       <div className={`${styles.select_wrap} select_wrap`}>
        <label>{t('adminPanel.form.articleCategory')}*</label>
        <Field
         name='type'
         component='select'
         validate={isRequired}
         onChange={(e: any) => {
          form.change('type', e.target.value);
         }}
        >
         <option value='' disabled>
          {t('adminPanel.form.chooseCategory')}
         </option>
         {articleTypesLocales?.map((type) => (
          <option value={type.value} key={type.value}>
           {t(type.title)}
          </option>
         ))}
        </Field>
        <Field
         name='type'
         subscription={{ error: true }}
         render={({ meta }) =>
          submitFailed &&
          meta.error && <span className='error'>{t(meta.error)}</span>
         }
         className='error'
        />
       </div>
      </div>
      {values.type === ArticleTypes.POST && (
       <div className={styles.select_services}>
        <CustomSelectField<IService>
         setSelectedItems={setSelectedServices}
         selectedItems={selectedServices}
         propItems={services}
         fieldLabel={'adminPanel.form.relatedServices'}
         fieldOption={'adminPanel.form.chooseService'}
         fieldName='serviceIds'
        />
       </div>
      )}
      <Field name='introduction'>
       {({ input, meta }) => (
        <div
         className={`${styles.input_wrap} ${
          meta.error && meta.touched ? styles.form_input_error : ''
         }`}
        >
         <label>{t('adminPanel.form.articleIntroduction')}</label>
         <textarea
          {...input}
          placeholder={t('adminPanel.form.articleIntroduction')}
         />
         {meta.error && meta.touched && <span>{t(meta.error)}</span>}
        </div>
       )}
      </Field>
      <Field name='firstParagraph' validate={isRequired}>
       {({ input, meta }) => (
        <div
         className={`${styles.input_wrap} ${
          meta.error && meta.touched ? styles.form_input_error : ''
         }`}
        >
         <label>{t('adminPanel.form.articleFirstParagraph')}*</label>
         <textarea
          {...input}
          placeholder={t('adminPanel.form.articleFirstParagraph')}
         />
         {meta.error && meta.touched && <span>{t(meta.error)}</span>}
        </div>
       )}
      </Field>
      <Field name='secondParagraph'>
       {({ input, meta }) => (
        <div
         className={`${styles.input_wrap} ${
          meta.error && meta.touched ? styles.form_input_error : ''
         }`}
        >
         <label>{t('adminPanel.form.articleSecondParagraph')}</label>
         <textarea
          {...input}
          placeholder={t('adminPanel.form.articleSecondParagraph')}
         />
         {meta.error && meta.touched && <span>{t(meta.error)}</span>}
        </div>
       )}
      </Field>
      <Field name='conclusion'>
       {({ input, meta }) => (
        <div
         className={`${styles.input_wrap} ${
          meta.error && meta.touched ? styles.form_input_error : ''
         }`}
        >
         <label>{t('adminPanel.form.articleConclusion')}</label>
         <textarea
          {...input}
          placeholder={t('adminPanel.form.articleConclusion')}
         />
         {meta.error && meta.touched && <span>{t(meta.error)}</span>}
        </div>
       )}
      </Field>
      <div className={styles.checkbox_wrap}>
       <label>{t('adminPanel.form.isPublished')}</label>
       <Field
        name='isPublished'
        component='input'
        type='checkbox'
        defaultValue={true}
       />
      </div>

      {currentUser?.role === userRoles.admin && (
       <Button
        type='submit'
        className={'orange'}
        label={t('buttons.save')}
        customStyle={styles.form_button}
        disabled={pristine || submitting}
       />
      )}
     </form>
    )}
   />
  </div>
 );
};
